import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useQuery } from '@tanstack/react-query';
import { Player } from './Player';
import type { PlayerProps } from './types';
import { fetchEventData, type EventData, EventsSortDirection } from './api/event';
import { fetchVideosList, Playlist, type VideoData, type ConcurrencySession, ConcurrencyConnectionState } from './api/video';
import { Loading, ErrorScreen, TitleAndDescription } from './components';
import { findHLSPlaylist } from './helper';
import useMessages, { type Translations } from './messages/useMessages';
import { useConcurrencySession, type SessionStartedInfo } from './hooks/useConcurrencySession';
import { getDeviceId } from './utils/deviceId';


export interface EventProps extends Omit<PlayerProps, 'src' | 'drmConfig'> {
  publicKey: string;
  eventId: string;
  hideTitle?: boolean;
  locale?: string;
  filter?: string;
  order?: EventsSortDirection;
  events?: {
    onEventData?: (event: EventData) => void;
    onVideoData?: (video: VideoData) => void;
    onEmptyPlaylists?: () => void;
    onError?: (error: Error) => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
    onLoadStart?: () => void;
    onCanPlay?: () => void;
    /** Fires once the Shaka player instance is created (before manifest load).
     * The instance exposes `player.mux.updateData(...)` for mid-view metadata
     * updates (e.g. attaching consent-gated ids after a CMP grant). */
    onPlayerReady?: (player: any) => void;
    onAdStart?: () => void;
    onAdComplete?: () => void;
    onAdError?: (error: any) => void;
    onAdSkipped?: () => void;
    onAdPaused?: () => void;
    onAdResumed?: () => void;
    onAdProgress?: (adProgressData: any) => void;
    onAllAdsCompleted?: () => void;
    // Concurrency session events
    /** Called when concurrency session is established and playback can begin */
    onConcurrencySessionStarted?: (info: SessionStartedInfo) => void;
    /** Called when session is kicked - playback will be stopped */
    onConcurrencyKicked?: (reason: string, message: string) => void;
    /** Called when the number of active sessions changes */
    onConcurrencySessionCountChanged?: (activeSessions: number, limit: number) => void;
    /** Called when concurrency connection state changes */
    onConcurrencyStateChanged?: (state: ConcurrencyConnectionState) => void;
  };
  className?: string;
  settings?: {
    backgroundImageUrl?: string;
    showCountdownAnimation?: boolean;
  };
  auth?: {
    mottoToken?: string;
    userId?: string;
  };
  // TanStack Query options
  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
    retry?: number;
    retryDelay?: number;
  };
  adsEnabled?: boolean;
}

export const Event: React.FC<EventProps> = ({
  publicKey,
  eventId,
  events,
  hideTitle,
  locale = 'en',
  order,
  filter,
  className,
  settings,
  auth,
  deviceId,
  queryOptions = {},
  adsEnabled = false,
  ...props
}) => {
  // Get the effective deviceId - use provided one or auto-generate
  // This must be consistent between the API call and WebSocket connection
  const effectiveDeviceId = useMemo(() => getDeviceId(deviceId), [deviceId]);

  // Event data fetching
  const {
    data: eventData,
    isLoading: isEventLoading,
    error: eventError
  } = useQuery<EventData>({
    queryKey: ['event', publicKey, eventId, filter, order, locale],
    queryFn: () => fetchEventData(publicKey, eventId, undefined, filter, order, locale),
    enabled: !!publicKey && !!eventId,
    staleTime: queryOptions.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: queryOptions.cacheTime ?? 10 * 60 * 1000, // 10 minutes
    retry: queryOptions.retry ?? 3,
    retryDelay: queryOptions.retryDelay ?? ((attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000))
  });

  const [activePlaylist, setActivePlaylist] = useState<Playlist | null>();
  const [activeVideoId, setActiveVideoId] = useState<string | null>();
  const [activeConcurrencySession, setActiveConcurrencySession] = useState<ConcurrencySession | undefined>();
  const videoIds = eventData?.videoIds ?? [];
  // Videos list fetching
  const {
    data: videosData,
    isLoading: videosIsLoading,
    error: videosError,
  } = useQuery<VideoData[]>({
    queryKey: ['videos-list', publicKey, videoIds, auth?.mottoToken, adsEnabled, locale, effectiveDeviceId],
    queryFn: () => fetchVideosList(publicKey, videoIds, auth?.mottoToken, 0, 0, adsEnabled, locale, effectiveDeviceId),
    enabled: !!publicKey && videoIds.length > 0,
    refetchInterval: activePlaylist === null ? 30000 : false,
    staleTime: queryOptions.staleTime ?? 5 * 60 * 1000,
    gcTime: queryOptions.cacheTime ?? 10 * 60 * 1000,
    retry: queryOptions.retry ?? 3,
    retryDelay: queryOptions.retryDelay ?? ((attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000))
  });

  const [loadingApisState, setLoadingApisState] = useState(true);

  // Process videos data to find active playlist
  useEffect(() => {
    if (videosData !== undefined) {
      setLoadingApisState(false);

      const videosWithPlaylists = videosData.filter(
        (video) => video.playlists && video.playlists.length > 0
      );

      if (videosWithPlaylists.length > 0) {
        let hlsPlaylistFound = false;
        // Iterate to find the first HLS playlist
        for (const video of videosWithPlaylists) {
          const activePlaylist = findHLSPlaylist(video);
          const activePlaylistUrl = (
              activePlaylist?.url ??
              activePlaylist?.drm?.widevine?.playlistUrl ??
              activePlaylist?.drm?.playready?.playlistUrl ??
              activePlaylist?.drm?.fairplay?.playlistUrl);
          const activePlaylistHasUrl = !!activePlaylistUrl;

          if (activePlaylist && activePlaylistHasUrl) {
            setActivePlaylist(activePlaylist);
            setActiveVideoId(video.id);
            setActiveConcurrencySession(video.concurrencySession);
            hlsPlaylistFound = true;
            break;
          }
        }
        if (!hlsPlaylistFound) {
          setActivePlaylist(null);
          setActiveVideoId(null);
          setActiveConcurrencySession(undefined);
        }
      } else {
        setActivePlaylist(null);
        setActiveVideoId(null);
        setActiveConcurrencySession(undefined);
      }
    } else if (eventData && (!eventData.videoIds || eventData.videoIds.length === 0)) {
      setLoadingApisState(false);
      setActivePlaylist(null);
      setActiveVideoId(null);
      setActiveConcurrencySession(undefined);
    }
  }, [videosData, eventData]);
  
  const { t } = useMessages(locale);

  // ============================================================================
  // Concurrency Session Management
  // ============================================================================

  /**
   * Connect to concurrency session WebSocket if required.
   *
   * Security: The hook will enforce that playback is only allowed when
   * the WebSocket connection is established (if concurrency control is required).
   * If connection fails or user is kicked, isPlaybackAllowed will be false.
   */
  const concurrencyOptions = useMemo(() => ({
    // Use the same deviceId that was sent to the API
    deviceId: effectiveDeviceId,
    // Forward concurrency events to parent
    onSessionStarted: events?.onConcurrencySessionStarted,
    onKicked: events?.onConcurrencyKicked,
    onSessionCountChanged: events?.onConcurrencySessionCountChanged,
    onStateChanged: events?.onConcurrencyStateChanged,
    onError: (err: Error) => {
      console.error('[Event] Concurrency session error:', err);
      events?.onError?.(err);
    },
  }), [effectiveDeviceId, events]);

  const {
    isPlaybackAllowed,
    wasKicked,
    kickedReason,
    connectionState: concurrencyConnectionState,
  } = useConcurrencySession(activeConcurrencySession, concurrencyOptions);

  // Notify parent of event data
  useEffect(() => {
    if (events?.onEventData && eventData) {
      events.onEventData(eventData);
    }
  }, [eventData, events]);

  // Notify parent of video data
  useEffect(() => {
    if (events?.onVideoData && activeVideoId && videosData) {
      const activeVideo = videosData.find((video) => video.id === activeVideoId);
      if (activeVideo) {
        events.onVideoData(activeVideo);
      }
    }
  }, [activeVideoId, videosData, events]);

  // All state and hooks must be defined before any conditional returns
  const [error, setError] = useState<Error | null>(null);
  const [loadingPlaylist, setLoadingPlaylist] = useState(true);
  const videosDataError = videosData?.some((video) => !!video.error);

  // Error handling
  useEffect(() => {
    // Defer error evaluation until loading states settle to avoid flashing
    // error. Query-level errors must NOT be deferred: they are terminal
    // (retries exhausted) and nothing clears loadingApisState when a fetch
    // fails, so waiting on it would show the loading screen forever.
    const hasFetchError = Boolean(eventError || videosError);
    if (!hasFetchError && (isEventLoading || videosIsLoading || loadingApisState)) {
      return;
    }

    const hasPlayablePlaylist = Boolean(activePlaylist);

    if (eventError || videosError || (videosDataError && !hasPlayablePlaylist)) {
      const firstVideoError = videosData?.find((video) => !!video.error)?.error;
      const errorObj =
        eventError ||
        videosError ||
        (firstVideoError && new Error(firstVideoError)) ||
        new Error('default');

      setError(errorObj);
      if (events?.onError) {
        events.onError(errorObj);
      }
    } else {
      setError(null);
    }
  }, [
    eventError,
    videosError,
    videosDataError,
    videosData,
    events,
    activePlaylist,
    isEventLoading,
    videosIsLoading,
    loadingApisState
  ]);
  
  // Loading state management
  useEffect(() => {
    const eventLoadedWithNoVideos =
      !isEventLoading &&
      eventData &&
      eventData.videoIds &&
      (!eventData.videoIds || eventData?.videoIds?.length === 0) &&
      !loadingApisState;

    const allApisLoadedWithPotentialVideos =
      !isEventLoading &&
      !videosIsLoading &&
      eventData &&
      !loadingApisState;

    if (eventLoadedWithNoVideos || allApisLoadedWithPotentialVideos) {
      setLoadingPlaylist(false);
    }
  }, [isEventLoading, videosIsLoading, eventData, loadingApisState]);

  // The error screen takes precedence over the pre-event card: if the event's
  // videos are errored we surface the error rather than the countdown.
  // NOTE: all early returns below must stay after every hook above so the hook
  // order never changes between renders (React error #300).
  const startTimeMs = eventData?.startTime
    ? new Date(eventData.startTime).getTime()
    : NaN;
  // Show the pre-event card only when there is no playable playlist yet. If a
  // playlist is already available we play it, even if the scheduled start time
  // is still in the future. While the videos list is still loading we don't
  // know either way, so hold off (the Loading screen renders instead) to
  // avoid flashing the card before the player mounts. A videos fetch error
  // never settles these flags, but that path is covered by the error screen
  // above taking precedence.
  const videosSettled = !videosIsLoading && !loadingApisState;
  const hasPreEventCard =
    !!eventData && startTimeMs > Date.now() && !activePlaylist && videosSettled;
  

  // Error state — takes precedence over the pre-event countdown.
  if (error) {
    const title = t(error.message as keyof Translations)?.length ?
      t(error.message as keyof Translations) : t("DEFAULT_ERROR");
    const description = t(`${error.message}_DESCRIPTION` as keyof Translations)?.length ?
      t(`${error.message}_DESCRIPTION` as keyof Translations) : t("DEFAULT_ERROR_DESCRIPTION");

    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full">
          <ErrorScreen
            title={title}
            description={description}
          />
        </div>
      </div>
    );
  }

  if (hasPreEventCard && eventData) {
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full h-full">
          <PreEvent
            event={eventData}
            hideTitle={hideTitle}
            locale={locale}
            backgroundImageUrl={settings?.backgroundImageUrl}
            showAnimation={settings?.showCountdownAnimation}
          />
        </div>
      </div>
    );
  }
  
  // Handle empty playlists
  if (!loadingPlaylist && eventData && !activePlaylist && events?.onEmptyPlaylists) {
    events.onEmptyPlaylists();
  }

  // Loading state
  if (loadingPlaylist) {
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full aspect-video bg-[#151515]">
          <Loading />
        </div>
      </div>
    );
  }

  // ============================================================================
  // Concurrency Control Check (SECURITY)
  // ============================================================================
  /**
   * If concurrency control is required but playback is not allowed,
   * show an error screen. This prevents bypassing concurrency limits
   * by blocking the WebSocket connection.
   */
  if (activeConcurrencySession && !isPlaybackAllowed) {
    // Determine the appropriate error message
    const isConnecting = concurrencyConnectionState === ConcurrencyConnectionState.CONNECTING;

    // If still connecting, show loading instead of error
    if (isConnecting) {
      return (
        <div className={twMerge("", className)}>
          <div className="relative w-full aspect-video bg-[#151515]">
            <Loading />
          </div>
        </div>
      );
    }

    // User was kicked or connection failed - show error
    // Map kickedReason to appropriate translation keys
    const isConnectionFailure = kickedReason === 'connection_failed';
    const errorTitle = isConnectionFailure
      ? t("CONCURRENCY_CONNECTION_FAILED" as keyof Translations) || "Connection Error"
      : t("CONCURRENCY_KICKED" as keyof Translations) || "Playback Stopped";

    const errorDescription = isConnectionFailure
      ? t("CONCURRENCY_CONNECTION_FAILED_DESCRIPTION" as keyof Translations) || "Unable to establish a connection. Please try again."
      : t("CONCURRENCY_KICKED_DESCRIPTION" as keyof Translations) || "Your session was ended. This may happen if you started watching on another device.";

    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full">
          <ErrorScreen
            title={errorTitle}
            description={errorDescription}
          />
        </div>
      </div>
    );
  }

  // Active video player
  if (activePlaylist && activeVideoId && videosData) {
    const activeVideo = videosData.find((video) => video.id === activeVideoId);
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full h-full">
          <Player
            poster={eventData?.posterUrl}
            {...props}
            src={activePlaylist}
            managedMode={true}
            className={twMerge(className, "peer aspect-video")}
            events={events}
            locale={locale}
            containerClassName="w-full h-full"
            publicKey={publicKey}
            auth={auth}
            {...(adsEnabled && activeVideo?.ad?.adTagUrl ? { imaConfig: { adTagUrl: activeVideo.ad.adTagUrl } } : {})}
          />
        </div>
          {!hideTitle && eventData && (
            <TitleAndDescription
              title={eventData.title}
              description={eventData.description || ''}
              dateTime={eventData.startTime}
              locale={locale}
            />
          )}
      </div>
    );
  }

  // Pre-event countdown
  if (eventData) {
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full h-full">
          <PreEvent 
            event={eventData} 
            hideTitle={hideTitle} 
            locale={locale} 
            backgroundImageUrl={settings?.backgroundImageUrl} 
            showAnimation={settings?.showCountdownAnimation} 
          />
        </div>
      </div>
    );
  }

  return null;
};

// Pre-event countdown component
function PreEvent({ 
  event, 
  locale = 'en', 
  hideTitle, 
  backgroundImageUrl, 
  showAnimation = true 
}: { 
  event: EventData; 
  locale?: string; 
  hideTitle?: boolean; 
  backgroundImageUrl?: string; 
  showAnimation?: boolean;
}): JSX.Element {
  const date = new Date(event.startTime);
  const now = new Date();
  const [remainingTime, setRemainingTime] = useState(
    date.getTime() - now.getTime()
  );
  const shouldBeStarted = remainingTime < 0;
  const { t } = useMessages(locale);
  useEffect(() => {
    const interval = setInterval(() => {
      if (remainingTime < 0) {
        clearInterval(interval);
      } else {
        setRemainingTime(date.getTime() - new Date().getTime());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [date, remainingTime]);

  const renderCountdown = useCallback(() => {
    if (shouldBeStarted) {
      return <span className="text-base-content text-xl">{t("EVENT_NOT_STARTED")}</span>;
    }

    const seconds = Math.floor(remainingTime / 1000) % 60;
    const minutes = Math.floor(remainingTime / 1000 / 60) % 60;
    const hours = Math.floor(remainingTime / 1000 / 60 / 60) % 24;
    const days = Math.floor(remainingTime / 1000 / 60 / 60 / 24);

    return (
      <div className="grid grid-flow-col gap-2 md:gap-5 text-center auto-cols-max">
        <div className="flex flex-col">
          {/* daisyUI's `countdown` only supports 2 digits, so days (which can be
              3+) is plain text. Mimic countdown's box model (inline-flex,
              line-height:1em, child height:1em) so it aligns with the others. */}
          <span className="font-mono text-lg md:text-5xl inline-flex leading-[1em] justify-center">
            <span
              className="inline-block h-[1em]"
              aria-live="polite"
              aria-label={days.toString()}
            >
              {days.toString()}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("DAYS")}</span>
        </div>
        <div className="flex flex-col">
          <span className="countdown font-mono text-lg md:text-5xl justify-center">
            <span 
              style={{ "--value": hours } as CSSProperties} 
              aria-live="polite" 
              aria-label={hours.toString()}
            >
              {hours?.toString()?.padStart(2, '0')}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("HOURS")}</span>
        </div>
        <div className="flex flex-col">
          <span className="countdown font-mono text-lg md:text-5xl justify-center">
            <span 
              style={{ "--value": minutes } as CSSProperties} 
              aria-live="polite" 
              aria-label={minutes.toString()}
            >
              {minutes?.toString()?.padStart(2, '0')}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("MINUTES")}</span>
        </div>
        <div className="flex flex-col">
          <span className="countdown font-mono text-lg md:text-5xl justify-center">
            <span 
              style={{ "--value": seconds } as CSSProperties} 
              aria-live="polite" 
              aria-label={seconds.toString()}
            >
              {seconds?.toString()?.padStart(2, '0')}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("SECONDS")}</span>
        </div>
      </div>
    );
  }, [remainingTime, shouldBeStarted, t]);

  return (
    <>
      {event?.posterUrl ? (
        <>
          <div
            className="relative overflow-hidden bg-base-200 aspect-video text-base-content w-full h-full flex justify-center items-center flex-col bg-no-repeat bg-cover md:rounded-2xl"
            style={{
              backgroundImage: `url(${event.posterUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover'
            }}
          >
            {/* <div className="absolute inset-0 bg-black bg-opacity-40"></div> */}
            <div className="relative z-10">
              {renderCountdown()}
            </div>
          </div>
          {!hideTitle && (
            <TitleAndDescription
              title={event.title}
              description={event.description || ''}
              dateTime={event.startTime}
              locale={locale}
            />
          )}
        </>
      ) : (
        <>
          <div
            className="relative overflow-hidden md:rounded-2xl! bg-base-200 aspect-video text-base-content flex flex-col justify-center items-center w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : '',
            }}
          >
            {/* {backgroundImageUrl && <div className="absolute inset-0 bg-black bg-opacity-40"></div>} */}
            <div className="relative z-10">
              {renderCountdown()}
            </div>
          </div>
          {!hideTitle && (
            <TitleAndDescription
              title={event.title}
              description={event.description || ''}
              dateTime={event.startTime}
              locale={locale}
            />
          )}
        </>
      )}
    </>
  );
}